import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { COMPANY, IMG } from "../data";

const lines = ["Voor iedere klus,", "de juiste vakman."];

const parent = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12, delayChildren: 0.5 } },
};
const child = {
  hidden: { y: "110%" },
  visible: { y: 0, transition: { duration: 1, ease: [0.16, 1, 0.3, 1] } },
};

export const Hero = () => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlayY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative min-h-screen overflow-hidden">
      {/* Parallax image */}
      <motion.div style={{ y, scale }} className="absolute inset-0 -z-10">
        <img src={IMG.hero} alt="DOPMAN bouwproject" className="w-full h-[115%] object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg)] via-[var(--bg)]/20 to-black/30" />
      </motion.div>

      <div className="max-w-[1600px] mx-auto px-6 md:px-10 min-h-screen flex flex-col justify-end pb-16 pt-40">
        {/* Overline */}
        <motion.div
          style={{ y: overlayY }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 1 }}
          className="flex items-center gap-4 mb-8"
        >
          <span className="h-px w-16 bg-[var(--turq)]" />
          <span className="font-mono-tech text-sm md:text-base uppercase tracking-[0.22em] text-black/90 font-medium">
            Aannemerij · Sinds {COMPANY.since} · Zuid-Holland
          </span>
        </motion.div>

        {/* Kinetic headline */}
        <motion.h1
          variants={parent}
          initial={reduce ? "visible" : "hidden"}
          animate="visible"
          className="font-display font-semibold tracking-tighter leading-[0.92] text-[11.5vw] md:text-[7.4vw] lg:text-[6.4rem] max-w-6xl"
        >
          {lines.map((line, i) => (
            <span key={i} className="reveal-line">
              <motion.span variants={child} className="block">
                {i === 1 ? (
                  <>
                    de juiste <span className="text-[var(--orange)]">vakman.</span>
                  </>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </motion.h1>

        <div className="mt-10 flex flex-col md:flex-row md:items-end justify-between gap-8">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.6, duration: 0.9 }}
            className="max-w-md text-lg text-black/70 leading-relaxed"
          >
            Bouwprojecten van fundament tot eindafwerking. Drie gespecialiseerde afdelingen, één betrouwbare hoofdaannemer.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.8, duration: 0.9 }}
            className="flex items-center gap-4"
          >
            <a
              href="#contact"
              data-testid="hero-cta-button"
              className="inline-flex items-center gap-2 bg-[var(--ink)] text-white px-8 py-4 text-sm font-medium hover:bg-[var(--orange)] hover:-translate-y-0.5 transition-[background-color,transform] duration-300"
            >
              Start uw project <ArrowUpRight size={18} />
            </a>
            <a
              href="#disciplines"
              data-testid="hero-secondary-button"
              className="inline-flex items-center gap-2 border border-black/20 px-8 py-4 text-sm font-medium hover:border-black hover:-translate-y-0.5 transition-[border-color,transform] duration-300"
            >
              Onze disciplines
            </a>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.4, duration: 1 }}
          className="mt-14 flex items-center gap-2 text-black/40 font-mono-tech text-xs uppercase tracking-[0.2em]"
        >
          <ArrowDown size={14} className="animate-bounce" /> Scroll
        </motion.div>
      </div>
    </section>
  );
};
